import { uniq } from 'ramda'

import { mapAndValidateFilters } from '../filters/utils'
import { ValidatedCustomerModel } from '../transforms/user.transforms'
import { deserialize } from '../utils/deserialize'
import { AdditionalRequestConfig, request } from './request'

export interface Filters {
  customerIds?: string[]
  customerName?: string
  customerNumber?: string[]
  distributionChannels?: string[]
  divisions?: string[]
  salesGroupIds?: string[]
  salesOrganisations?: string[]
}

export const fetchCustomerContexts = async (
  filters?: Filters,
  config: AdditionalRequestConfig = {},
): Promise<ValidatedCustomerModel[]> => {
  const filterMap = {
    customerIds: 'filter[id.in]',
    customerName: 'filter[customerName.icontains]',
    customerNumbers: 'filter[customerNumber.in]',
    distributionChannels: 'filter[salesGroup.distributionChannel.in]',
    divisions: 'filter[salesGroup.division.in]',
    salesGroupIds: 'filter[salesGroup.id.in]',
    salesOrganisations: 'filter[salesGroup.salesOrganization.in]',
  } as const

  const validatedFilters = mapAndValidateFilters(filters, filterMap)

  try {
    const response = await request({
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'GET',
      params: Object.assign(
        {
          include: 'salesGroup,locations',
        },
        validatedFilters,
      ),
      url: `${config.baseUrl || ''}/api/customer-contexts`,
    })

    return deserialize<ValidatedCustomerModel[]>(response?.data)
  } catch (e) {
    // User does not have access to any of the customerContexts
    return []
  }
}

interface FetchCustomersFilters {
  customerContextIds?: string[]
  customerName?: string
  customerNumbers?: string[]
}

interface FetchCustomersParams {
  config?: AdditionalRequestConfig
  filters: FetchCustomersFilters
  salesGroupIds?: string[]
}

export const fetchCustomers = async ({
  filters,
  salesGroupIds,
  config = {},
}: FetchCustomersParams): Promise<ValidatedCustomerModel[]> => {
  const filterMap = {
    customerContextId: 'filter[id.in]',
    customerName: 'filter[customerName.icontains]',
    customerNumbers: 'filter[customerNumber.icontains]',
    salesGroupIds: 'filter[salesGroup.id.in]',
  } as const

  const assignedFiltersCount = Object.values(filters).filter(
    value => value !== undefined,
  ).length

  if (assignedFiltersCount === 0 || assignedFiltersCount > 1) {
    throw new Error('Only one filter should be assigned at a time')
  }

  const { customerContextIds, customerName, customerNumbers } = filters

  try {
    const response = await request({
      headers: {
        'Content-Type': 'application/json',
        ...(config.headers || {}),
      },
      method: 'GET',
      params: {
        include: 'salesGroup,locations',
        [filterMap.customerContextId]:
          customerContextIds && uniq(customerContextIds).join(','),
        [filterMap.customerName]: customerName,
        [filterMap.customerNumbers]:
          customerNumbers && uniq(customerNumbers).join(','),
        [filterMap.salesGroupIds]:
          salesGroupIds && uniq(salesGroupIds).join(','),
      },
      url: `${config.baseUrl || ''}/api/customer-contexts`,
    })

    return deserialize<ValidatedCustomerModel[]>(response?.data)
  } catch (e) {
    // User does not have access to any of the customerContexts
    return []
  }
}
